/**
 * O que faz o guia passo a passo andar: ele avança pelo que a pessoa faz (a janela abriu, a busca
 * começou, a lista chegou), e não por "Próximo" (proposta, seção 1). Lê o que o painel já sabe:
 * a janela e a captura (captureStore), a janela da extensão (uiStore), a rota, as listas e, quando
 * só a tela sabe (a explicação da aba), o DOM. O guia termina quando a lista da busca abre.
 *
 * Com a extensão 8.1 (local-first), a captura diz quando terminou, pausou ou voltou vazia. Com a
 * publicada, só a contagem e o fim; o resultado sai da lista (levantamento, seção 4.1). Depois do
 * "Ver leads" da aba da busca, que recarrega o painel, o passo vem do navegador e o resultado, da
 * lista criada depois do começo da busca.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLists } from '../../api/useLists';
import { useCaptureStore } from '../../stores/captureStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useUiStore } from '../../stores/uiStore';
import { EXTENSION_MODAL_ID } from '../../hooks/useRequireExtension';
import { can, isTrial } from '../../lib/entitlements';
import type { SearchList } from '../../types/searchList';
import { useGuiaStore } from './guiaStore';
import { abrirBuscaDoGuia, concluirGuia, registrarPasso } from './guiaAcoes';
import { acharAncora } from './ancora';
import { passoNaJanela, type GuiaModulo } from './passos';

/** Sem contato novo por este tempo, o cartão pergunta "Parece parado?". */
export const PARADO_MS = 3 * 60 * 1000;
/** Depois de recarregar, espera a extensão e a captura se apresentarem antes de concluir. */
const ESPERA_RECARGA_MS = 6000;
/** Margem entre o começo da busca no painel e o `startedAt` da lista no servidor. */
const MARGEM_LISTA_MS = 2 * 60 * 1000;

export const ALVO = {
  explicacao: '.ic-explicacao',
  ddd: '[data-guia="campo-ddd"]',
} as const;

export function paginaDeListas(modulo: GuiaModulo): string {
  return modulo === 'places' ? '/places/lists' : `/${modulo}`;
}

/** A lista que a pessoa abriu, pela rota (`/places?list=…` ou `/list/<tipo>/<id>/leads`). */
export function listaAbertaNaRota(
  pathname: string,
  search: string,
  modulo: GuiaModulo,
): string | null {
  if (modulo === 'places') {
    if (pathname !== '/places') return null;
    const id = new URLSearchParams(search).get('list');
    return id && id !== 'all' ? id : null;
  }
  const m = /^\/list\/(phones|emails)\/([^/]+)\/leads$/.exec(pathname);
  return m && m[1] === modulo ? decodeURIComponent(m[2]!) : null;
}

/** A lista da busca do guia: a mais nova do tipo, criada depois do começo da busca. */
export function listaDaBusca(
  listas: readonly SearchList[] | undefined,
  inicioBusca: number | null,
  listaId: string | null,
): SearchList | null {
  if (!listas?.length) return null;
  if (listaId) {
    const exata = listas.find((l) => l._id === listaId);
    if (exata) return exata;
  }
  const desde = inicioBusca ? inicioBusca - MARGEM_LISTA_MS : 0;
  const candidatas = listas
    .filter((l) => Date.parse(l.startedAt) >= desde)
    .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
  return candidatas[0] ?? null;
}

export interface GuiaVivo {
  /** Contatos da busca agora (ou do fim dela). */
  contatos: number;
  /** A busca está há mais de 3 minutos sem contato novo. */
  parado: boolean;
  lista: SearchList | null;
}

export function useGuiaControle(): GuiaVivo {
  const session = useSessionStore((s) => s.session);
  const userId = session?.userId ?? '';
  const carregar = useGuiaStore((s) => s.carregar);
  const status = useGuiaStore((s) => s.status);
  const passo = useGuiaStore((s) => s.passo);
  const modulo = useGuiaStore((s) => s.modulo);
  const inicioBusca = useGuiaStore((s) => s.inicioBusca);
  const listaId = useGuiaStore((s) => s.listaId);
  const contatosSalvos = useGuiaStore((s) => s.contatos);
  const ir = useGuiaStore((s) => s.ir);

  const captureWindow = useCaptureStore((s) => s.captureWindow);
  const active = useCaptureStore((s) => s.active);
  const count = useCaptureStore((s) => s.count);
  const countModule = useCaptureStore((s) => s.countModule);
  const extensionPresent = useCaptureStore((s) => s.extensionPresent);
  const activeModal = useUiStore((s) => s.activeModal);
  const location = useLocation();
  const listas = useLists(modulo ?? 'places');

  // A busca das listas muda de identidade a cada desenho: o fim da busca usa a mais recente.
  const refetchListas = useRef(listas.refetch);
  refetchListas.current = listas.refetch;

  const ligado = status === 'ativo';
  const contagem = modulo && countModule === modulo ? count : 0;

  useEffect(() => {
    if (userId) carregar(userId);
  }, [userId, carregar]);

  // Funil do guia: cada passo alcançado (proposta, seção 8).
  useEffect(() => {
    if (ligado) registrarPasso(passo, modulo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ligado, passo]);

  /** Fim da busca: aba fechada, contatos ou nada. */
  const concluirBusca = useCallback(async () => {
    const guia = useGuiaStore.getState();
    if (guia.status !== 'ativo' || !guia.modulo) return;
    const c = useCaptureStore.getState();
    if (c.phase === 'paused' && c.pauseReason === 'search_tab_closed') {
      guia.ir('aba_fechada');
      return;
    }
    const aoVivo = c.countModule === guia.modulo ? c.count : 0;
    const resultado = await refetchListas.current();
    const lista = listaDaBusca(resultado.data, guia.inicioBusca, guia.listaId);
    const n = Math.max(aoVivo, lista?.count ?? 0);
    if (useGuiaStore.getState().passo !== guia.passo) return;
    if (n > 0) {
      guia.ir(isTrial(useSessionStore.getState().session) ? 'aviso_teste' : 'abrir_lista', {
        contatos: n,
        listaId: lista?._id ?? null,
      });
    } else {
      guia.ir('nada', { contatos: 0 });
    }
  }, []);

  // Começo e fim da busca, pela captura (os dois fluxos marcam `active`).
  const ativoAntes = useRef(active);
  useEffect(() => {
    const antes = ativoAntes.current;
    ativoAntes.current = active;
    if (!ligado || !modulo) return;
    if (!antes && active && (passoNaJanela(passo) || passo === 'nada' || passo === 'aba_fechada')) {
      const comExplicacao = Boolean(acharAncora(ALVO.explicacao));
      ir(comExplicacao ? 'explicacao' : 'buscando', { inicioBusca: Date.now(), listaId: null });
      return;
    }
    if (antes && !active && (passo === 'buscando' || passo === 'explicacao')) void concluirBusca();
  }, [active, ligado, modulo, passo, ir, concluirBusca]);

  // "Parece parado?": 3 minutos sem contato novo enquanto a busca roda.
  const ultimaMudanca = useRef(Date.now());
  const [agora, setAgora] = useState(Date.now());
  useEffect(() => {
    ultimaMudanca.current = Date.now();
  }, [contagem, passo]);
  useEffect(() => {
    if (!ligado || passo !== 'buscando') return undefined;
    const id = window.setInterval(() => setAgora(Date.now()), 10_000);
    return () => window.clearInterval(id);
  }, [ligado, passo]);

  // O que só o tempo ou a tela dizem: avaliado a cada 400 ms enquanto o guia está ligado.
  const montadoEm = useRef(Date.now());
  const recargaResolvida = useRef(false);
  useEffect(() => {
    if (!ligado) return undefined;
    const avaliar = (): void => {
      const guia = useGuiaStore.getState();
      const c = useCaptureStore.getState();
      const p = guia.passo;
      const mod = guia.modulo;
      const explicacao = Boolean(acharAncora(ALVO.explicacao));
      const modalExtensao = useUiStore.getState().activeModal === EXTENSION_MODAL_ID;

      if (p === 'tipo') {
        const janela = c.captureWindow;
        if (janela === 'places' || janela === 'phones' || janela === 'emails') {
          guia.ir('campo_termo', { modulo: janela, inicioBusca: null, listaId: null, contatos: 0 });
        }
        return;
      }
      if (!mod) return;
      if (p === 'extensao') {
        if (c.extensionPresent === true && !modalExtensao) {
          if (!c.captureWindow) c.openCaptureWindow(mod, { janela: true });
          guia.ir('campo_termo');
        }
        return;
      }
      if (p === 'licenca') {
        if (can(useSessionStore.getState().session, 'capture.enabled')) abrirBuscaDoGuia(mod);
        return;
      }
      if (passoNaJanela(p)) {
        if (modalExtensao) guia.ir('extensao');
        else if (explicacao) guia.ir('explicacao');
        else if (p === 'campo_ddd' && c.captureWindow && !acharAncora(ALVO.ddd)) guia.ir('comecar');
        return;
      }
      if (p === 'explicacao') {
        if (modalExtensao) return;
        if (!explicacao) {
          if (c.active) guia.ir('buscando', { inicioBusca: guia.inicioBusca ?? Date.now() });
          else if (c.captureWindow) guia.ir('comecar');
        }
        return;
      }
      if (p === 'buscando') {
        // Painel recarregado no meio ou no fim da busca ("Ver leads"): espera a captura se
        // apresentar e, parada, conclui pela lista.
        if (
          !c.active &&
          !recargaResolvida.current &&
          Date.now() - montadoEm.current > ESPERA_RECARGA_MS
        ) {
          recargaResolvida.current = true;
          void concluirBusca();
        }
        return;
      }
      if (p === 'abrir_lista') {
        const aberta = listaAbertaNaRota(location.pathname, location.search, mod);
        if (aberta) concluirGuia(aberta);
      }
    };
    avaliar();
    const id = window.setInterval(avaliar, 400);
    return () => window.clearInterval(id);
  }, [
    ligado,
    passo,
    modulo,
    captureWindow,
    active,
    extensionPresent,
    activeModal,
    location.pathname,
    location.search,
    concluirBusca,
  ]);

  const lista = modulo ? listaDaBusca(listas.data, inicioBusca, listaId) : null;
  const contatos = passo === 'buscando' ? contagem : Math.max(contatosSalvos, lista?.count ?? 0);
  const parado =
    ligado && passo === 'buscando' && active && agora - ultimaMudanca.current > PARADO_MS;
  return { contatos, parado, lista };
}
